import Layout from '@src/components/Layout';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FarmersQuery } from '~/QueryHooks/Farmers';
import i18n from '~/components/i18n';
import { RouteTypes } from '~/routes/constants';
import TableComponent from '~/components/TableComponent';
import {
  LIGHT_USER_ROLE_SLUG,
  FARMER_MANAGER_ROLE_SLUG,
  MILK_MONEY_ACCOUNT_SLUG
} from '~/Constants';
import { StyledTab } from '~/styles/table';
import { useAuth } from '~/routes/components/AuthProvider';
import { DownloadService } from '~/api/services/downloadFiles';
import { Popup } from 'semantic-ui-react';
import CustomIcon from '~/components/CustomIcon';
import { Link, useSearchParams } from 'react-router-dom';
import SwissFarmerProviderComp from './SwissFarmerProviderComp';
import FilterValues from './FilterValues';
import BlockFarmer from '../Farmers/Dialogs/BlockFarmer';
import ImportFarmers from '../Farmers/Dialogs/ImportFarmers';
import DeleteFarmer from '../Farmers/Dialogs/DeleteFarmer';
import Actions from './Actions';
import Filters from './Filters';

const buildFilterQuery = (evsNumber: string, search: string, agisId: string) => {
  const params = new URLSearchParams()
  if (evsNumber) params.set('evsNumber', evsNumber)
  if (search) params.set('search', search)
  if (agisId) params.set('agisId', agisId)
  const query = params.toString()
  return query ? `&${query}` : ''
}

const getActions = (filterQuery: string) => [
  {
    popupMessage: i18n.t('Farmer details'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=profile-details${filterQuery}`
  },
  {
    popupMessage: i18n.t(''),
    type: 'link',
    name: 'sticky note',
    renderActions: () => <></>,
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=communication${filterQuery}`
  },
  {
    popupMessage: i18n.t('Article bookings'),
    type: 'link',
    name: 'shopping cart',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=article-bookings${filterQuery}`
  },
  {
    popupMessage: i18n.t('MGA attachments'),
    type: 'link',
    name: 'attach',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS
  },
  {
    popupMessage: i18n.t('Unblock'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'blockUnblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Block'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'blockUnblock',
    display: [1]
  }
]

const getActionsForLightUser = (filterQuery: string) => [
  {
    popupMessage: i18n.t('Farmer details'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=profile-details${filterQuery}`
  },
  {
    popupMessage: i18n.t('MGA attachments'),
    type: 'link',
    name: 'attach',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_FARMERS_MGA_ATTACHMENTS
  }
]

const ACTIVE_FARMERS_KEY = 'all_farmers'

const getTableColumnHeaders = () => [
  {
    name: i18n.t('EVS-Nr.'),
    key: 'evsNumber',
    sorted: 'desc',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=profile-details`
  },
  {
    name: i18n.t('Lieferanten-Nr.'),
    key: 'supplierNo',
    sorted: 'asc',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=profile-details`
  },
  {
    name: i18n.t('AGIS-ID Betrieb'),
    key: 'agisId',
    sorted: 'asc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    type: 'link',
    link: `${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW}?tab=profile-details`,
    sorted: 'asc'
  },
  {
    name: i18n.t('Street name'),
    key: 'streetName',
    type: 'street'
  },
  {
    name: i18n.t('Postleitzahl'),
    key: 'zip',
    sorted: 'asc'
  },
  {
    name: i18n.t('Ort'),
    key: 'city',
    sorted: 'asc'
  },
  {
    name: i18n.t('Actions')
  }
]

const defaultFilterValues = {
  page: 0,
  rows: 20,
  type: ACTIVE_FARMERS_KEY,
  column: '',
  sort: '',
  evsNumber: '',
  search: '',
  agisId: ''
}
const SwissFarmers: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = () => ({
    ...defaultFilterValues,
    evsNumber: searchParams.get('evsNumber') || '',
    search: searchParams.get('search') || '',
    agisId: searchParams.get('agisId') || ''
  });

  const [dataFiltered, setDataFiltered] = useState(getInitialFilters);
  const [blockModalData, setBlockModalData] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [deleteFarmerData, setDeleteFarmerData] = useState(null)
  const [openImportFarmersModal, setOpenImportFarmersModal] = useState(false)
  const { systemData: { mgaBilling, logged: { roleSlug }, modules } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())
  const hasMilkMoneyModule = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)

  const {
    isLoading: isBusyData,
    data: farmersData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmersQuery.getFarmersList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: farmersCount,
    isLoading: isBusyFarmersCount,
    refetch: fetchCount
  } = FarmersQuery.getFarmersQueryCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  const {
    data: farmersListOptions,
    refetch: refetchOptions,
    isLoading: isBusyFarmersList
  } = FarmersQuery.getFarmersListOptions();

  useEffect(() => {
    const defaultTableColumnHeaders = getTableColumnHeaders()
    setTableColumnHeaders(defaultTableColumnHeaders)
    refetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
      fetchCount()
    }
  }, [dataFiltered]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (dataFiltered.evsNumber) params.set('evsNumber', dataFiltered.evsNumber);
    if (dataFiltered.search) params.set('search', dataFiltered.search);
    if (dataFiltered.agisId) params.set('agisId', dataFiltered.agisId);
    setSearchParams(params, { replace: true });
  }, [dataFiltered.evsNumber, dataFiltered.search, dataFiltered.agisId]);

  useEffect(() => {
    if (farmersListOptions) {
      setActiveTabIndex(farmersListOptions?.types.findIndex(type => type.key === dataFiltered.type))
    }
  }, [farmersListOptions])

  function renderFilters() {
    return <Filters
      dataFiltered={dataFiltered}
      handleChangeData={(data) => {
        if (JSON.stringify(data) !== JSON.stringify(dataFiltered)) {
          setDataFiltered(data)
        }
      }}
    />
  }

  async function handleDownloadPdf(id) {
    setLoadingAction({
      type: 'pdf',
      id: id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFile(`farmers/${id}/generate-milk-payment-pdf`, 'term_pdf')
    } catch (error) {
      if (error.response.data) {
        // notify(error.response.data.message, 'error');
      } else {
        // notify(t('General error'), 'error');
      }
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'blockUnblock':
        return setBlockModalData(row)
      case 'delete':
        return setDeleteFarmerData(row)
      case 'pdf':
        return handleDownloadPdf(row?.id)
      default:
        return
    }
  }

  function getActionsBasedOnRole() {
    const filterQuery = buildFilterQuery(dataFiltered.evsNumber, dataFiltered.search, dataFiltered.agisId)
    switch (roleSlug) {
      case LIGHT_USER_ROLE_SLUG:
        return getActionsForLightUser(filterQuery)
      case FARMER_MANAGER_ROLE_SLUG:
        return [getActionsForLightUser(filterQuery)[0]]
      default:
        return getActions(filterQuery)
    }
  }

  const renderNoteAction = (row) => {
    const filterQuery = buildFilterQuery(dataFiltered.evsNumber, dataFiltered.search, dataFiltered.agisId)
    return row?.latestNote && <Link to={`${RouteTypes.DAIRY_FACTORY_FARMERS_SHOW.replace(':id', row.id)}?tab=communication${filterQuery}`}>
      <Popup
        content={row?.latestNote?.note}
        trigger={<CustomIcon
          style={{ cursor: 'pointer' }}
          name='sticky note'
          size='large'
        />}
      />
    </Link>
  }

  function renderContent() {
    let actionsFiltered = getActionsBasedOnRole()
    if (!hasMilkMoneyModule) {
      //@ts-ignore
      actionsFiltered = actionsFiltered?.filter(action => action.name !== 'shopping cart')
    }
    const noteActionIndex = actionsFiltered.findIndex(action => action.name === 'sticky note')
    if (noteActionIndex >= 0) {
      //@ts-ignore
      actionsFiltered[noteActionIndex].renderActions = renderNoteAction
    }

    return <TableComponent
      //@ts-ignore
      actions={mgaBilling ? actionsFiltered : actionsFiltered?.filter(action => action.name !== 'attach')}
      isFetchingData={isFetchingData}
      loadingAction={loadingAction}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = farmersListOptions.types.find((_, indexOption) => indexOption === activeIndex);
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        type: status.key,
        page: 0
      });
    }
  }

  function renderPanes() {
    const panes = farmersListOptions.types.map((option) => {
      const countKey = Object.keys(farmersCount).find((count) => count === option.key);
      return {
        menuItem: `${option.label}  (${farmersCount[countKey] || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <StyledTab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderActions() {
    return <Actions
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      dataFiltered={dataFiltered}
      defaultFilterValues={defaultFilterValues}
      lightUserLogged={lightUserLogged}
      handleImportClick={() => setOpenImportFarmersModal(true)}
      farmersListOptions={farmersListOptions}
    />
  }

  function renderDialogs() {
    return <>
      <DeleteFarmer
        deleteFarmerData={deleteFarmerData}
        handleClose={(submited) => {
          setDeleteFarmerData(null)
          if (submited) {
            fetchFilteredData();
            fetchCount()
          }
        }}
      />
      <ImportFarmers
        openImportFarmersModal={openImportFarmersModal}
        handleClose={() => setOpenImportFarmersModal(null)}
        farmersListOptions={farmersListOptions?.farmerGroups?.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name })) || []}
      />
      <BlockFarmer
        blockModalData={blockModalData}
        handleClose={(submited) => {
          setBlockModalData(null)
          if (submited) {
            fetchFilteredData()
          }
        }}
      />
    </>
  }

  const { filtered, rows } = farmersData ? farmersData : { filtered: 0, rows: [] };
  const loading = isBusyData || isBusyFarmersList || isBusyFarmersCount;

  return (
    <SwissFarmerProviderComp>
      <Layout
        isBusy={loading}
        segmented
        filters={farmersListOptions && !loading && renderFilters()}
        actions={renderActions()}
        title={t('Farmers')}
        actionAlignToTop
        actionNoWrap
      >
        {renderDialogs()}
        <FilterValues
          dataFiltered={dataFiltered}
          handleChangeDataFiltered={(data) => setDataFiltered(data)}
        />
        {farmersData && farmersListOptions && farmersCount && renderPanes()}
      </Layout>
    </SwissFarmerProviderComp>
  );
};

export default SwissFarmers;
